import { toast } from "sonner";
import api from "@/lib/api";

// Preferences + "entered" flags are scoped PER USER so one account never sees another's picks.
// They live in localStorage for instant UI AND are mirrored to the server (tied to the login)
// so they survive Incognito, cache clears and follow the user across devices.
let USER_KEY = "anon";
export function setChoicesUser(id) {
  USER_KEY = id ? String(id) : "anon";
  try { window.dispatchEvent(new Event("rp-choices-changed")); } catch { /* ignore */ }
  if (USER_KEY !== "anon") pullChoices();
}
const storeKey = () => `rp_choices_${USER_KEY}`;

export function getChoices() {
  try { return JSON.parse(localStorage.getItem(storeKey()) || "{}"); }
  catch { return {}; }
}

let pushTimer = null;
function pushChoices(c) {
  if (USER_KEY === "anon") return;
  clearTimeout(pushTimer);
  pushTimer = setTimeout(() => { api.put("/my-choices", { data: c }).catch(() => {}); }, 600);
}

// Load the server copy on login and merge it in (server wins for keys it has),
// then let every view refresh via the change event.
export async function pullChoices() {
  if (USER_KEY === "anon") return;
  try {
    const r = await api.get("/my-choices");
    const server = r && r.data && r.data.data;
    if (server && typeof server === "object") {
      const merged = { ...getChoices(), ...server };
      localStorage.setItem(storeKey(), JSON.stringify(merged));
      try { window.dispatchEvent(new Event("rp-choices-changed")); } catch { /* ignore */ }
    }
  } catch { /* offline / not logged in — keep local */ }
}

export function saveChoices(c) {
  localStorage.setItem(storeKey(), JSON.stringify(c));
  try { window.dispatchEvent(new Event("rp-choices-changed")); } catch { /* ignore */ }
  pushChoices(c);
}

// cycle a performance for a rodeo: none -> 1st -> 2nd -> none (legacy, used by desktop CalendarView)
export function cycleChoice(rodeoId, perfId) {
  const all = getChoices();
  const cur = all[rodeoId] || { pref1: null, pref2: null };
  if (cur.pref1 === perfId) cur.pref1 = null;
  else if (cur.pref2 === perfId) cur.pref2 = null;
  else if (!cur.pref1) cur.pref1 = perfId;
  else if (!cur.pref2) cur.pref2 = perfId;
  else cur.pref1 = perfId;
  all[rodeoId] = cur;
  saveChoices(all);
  return all;
}

// Save the entry confirmation number for a rodeo. A non-empty value means the rider has
// actually ENTERED (not just planned). Clearing it reverts the rodeo to "Planned".
export function setConfirmation(rodeoId, value) {
  const all = getChoices();
  const cur = all[rodeoId] || { pref1: null, pref2: null };
  const v = (value || "").trim();
  if (v) cur.confirmation = v; else delete cur.confirmation;
  all[rodeoId] = cur;
  saveChoices(all);
  return all;
}

// pick next available slot: fills 1st, then 2nd; clicking a picked perf clears it. Capped at two.
export function pickPerf(rodeoId, perfId) {
  const all = getChoices();
  const cur = all[rodeoId] || { pref1: null, pref2: null };
  if (cur.pref1 === perfId) cur.pref1 = null;
  else if (cur.pref2 === perfId) cur.pref2 = null;
  else if (!cur.pref1) cur.pref1 = perfId;
  else if (!cur.pref2) cur.pref2 = perfId;
  else {
    toast.error("You can pick two performances — unselect your 1st or 2nd first.");
    return all;
  }
  all[rodeoId] = cur;
  saveChoices(all);
  return all;
}

export function setPreference(rodeoId, perfId, n) {
  const all = getChoices();
  const cur = all[rodeoId] || { pref1: null, pref2: null };
  const field = n === 1 ? "pref1" : "pref2";
  cur[field] = cur[field] === perfId ? null : perfId;
  all[rodeoId] = cur;
  saveChoices(all);
  return all;
}

// Mark a specific PERFORMANCE as "entered". Only ONE performance per rodeo can be entered,
// so marking a new one clears any other. Stored as cur.entered = { [perfId]: true }.
export function toggleEnteredPerf(rodeoId, perfId) {
  const all = getChoices();
  const cur = all[rodeoId] || { pref1: null, pref2: null };
  const already = !!(cur.entered || {})[perfId];
  cur.entered = already ? {} : { [perfId]: true };
  all[rodeoId] = cur;
  saveChoices(all);
  return all;
}

// Is a whole rodeo "entered"? (any performance marked entered)
export function rodeoEntered(choices, rodeoId) {
  const ent = (choices[rodeoId] || {}).entered || {};
  return Object.values(ent).some(Boolean);
}

// Has the user marked this rodeo in ANY way (1st/2nd pick or entered)?
// Such rodeos are always kept visible across every view, even outside the date window.
export function rodeoTouched(choices, rodeoId) {
  const c = choices[rodeoId] || {};
  return !!(c.pref1 || c.pref2) || rodeoEntered(choices, rodeoId);
}
